import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import {
  alignVariants,
  type HeadingElement,
  toneVariants,
  truncateVariants,
  typographyWeightVariants,
} from "./shared";

const titleVariants = cva("", {
  variants: {
    size: {
      xl: [
        "font-(family-name:--font-family-title)",
        "text-(length:--font-size-title-xl)",
        "tracking-normal",
      ],
      lg: [
        "font-(family-name:--font-family-title)",
        "text-(length:--font-size-title-lg)",
        "tracking-[0.15px]",
      ],
      md: [
        "font-(family-name:--font-family-title)",
        "text-(length:--font-size-title-md)",
        "tracking-[0.15px]",
      ],
      sm: [
        "font-(family-name:--font-family-title)",
        "text-(length:--font-size-title-sm)",
        "tracking-[0.1px]",
      ],
      xs: [
        "font-(family-name:--font-family-title)",
        "text-(length:--font-size-title-xs)",
        "leading-(--line-height-16)",
        "tracking-[0.1px]",
      ],
    },
    tone: toneVariants,
    align: alignVariants,
    truncate: truncateVariants,
    weight: {
      medium: typographyWeightVariants.medium,
      semibold: typographyWeightVariants.semibold,
      bold: typographyWeightVariants.bold,
    },
  },
  defaultVariants: {
    size: "md",
    tone: "default",
    weight: "medium",
  },
});

type TitleSize = NonNullable<VariantProps<typeof titleVariants>["size"]>;
type TitleElement = HeadingElement | "span";

const defaultElementMap: Record<TitleSize, TitleElement> = {
  xl: "h4",
  lg: "h5",
  md: "h5",
  sm: "h6",
  xs: "h6",
};

export interface TitleProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "className">,
    VariantProps<typeof titleVariants> {
  /** Override the default HTML element */
  as?: TitleElement;
}

/**
 * Title component for card headers and subsection titles.
 * Uses Inter font family with medium weight.
 *
 * Sizes range from 22px (xl) down to 12px (xs).
 */
function Title({
  size = "md",
  tone,
  align,
  truncate,
  weight = "medium",
  as,
  ...props
}: TitleProps) {
  const defaultElement = size ? defaultElementMap[size] : "h5";
  const Element = as || defaultElement;

  return React.createElement(Element, {
    className: titleVariants({ size, tone, align, truncate, weight }),
    ...props,
  });
}

export { Title };
